/** One run of a word diff: text both sides share, or text only one side has. */
export type TextDiffSegment = {
  type: "same" | "added" | "removed";
  text: string;
};

const MAX_TOKENS = 2000;

/**
 * Word-level diff by longest common subsequence. Tokens keep their trailing
 * spaces and line breaks are separate tokens, so a changed last word never
 * carries its line break; joining the segments' text in order rebuilds
 * either side.
 * @param from - The old text
 * @param to - The new text
 * @returns Segments in reading order, adjacent runs of one type merged
 */
export function diffWords(from: string, to: string): TextDiffSegment[] {
  const a = tokenize(from);
  const b = tokenize(to);
  if (a.length > MAX_TOKENS || b.length > MAX_TOKENS) {
    return merge([
      { type: "removed", text: from },
      { type: "added", text: to },
    ]);
  }
  // lengths[i][j] is the LCS length of a[i..] and b[j..].
  const lengths = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      lengths[i][j] =
        a[i] === b[j]
          ? lengths[i + 1][j + 1] + 1
          : Math.max(lengths[i + 1][j], lengths[i][j + 1]);
    }
  }
  const segments: TextDiffSegment[] = [];
  let i = 0;
  let j = 0;
  while (i < a.length || j < b.length) {
    if (i < a.length && j < b.length && a[i] === b[j]) {
      segments.push({ type: "same", text: a[i++] });
      j++;
    } else if (
      i < a.length &&
      (j === b.length || lengths[i + 1][j] >= lengths[i][j + 1])
    ) {
      segments.push({ type: "removed", text: a[i++] });
    } else {
      segments.push({ type: "added", text: b[j++] });
    }
  }
  return merge(segments);
}

function tokenize(text: string): string[] {
  return text.match(/\n+|[^\S\n]+|\S+[^\S\n]*/g) ?? [];
}

function merge(segments: readonly TextDiffSegment[]): TextDiffSegment[] {
  return segments
    .filter((segment) => segment.text !== "")
    .reduce<TextDiffSegment[]>((merged, segment) => {
      const last = merged.at(-1);
      return last?.type === segment.type
        ? [
            ...merged.slice(0, -1),
            { type: last.type, text: last.text + segment.text },
          ]
        : [...merged, segment];
    }, []);
}
